/**
 * 全局轻量通知 (Toast) 队列。
 *
 * - 任何地方都可以 `useNotificationsStore().push('msg')`;
 * - 默认 3.5s 自动消失, 可通过 duration 改;
 * - severity 控制颜色 (error 红 / success 绿 / info 灰);
 * - 多条同时存在时, 后来的堆在下方 (Toast.vue 控制视觉)。
 */

import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ToastSeverity = 'info' | 'success' | 'error'

export interface Toast {
  id:       number
  text:     string
  severity: ToastSeverity
  timer?:   number
}

let _nextId = 1

export const useNotificationsStore = defineStore('notifications', () => {
  const toasts = ref<Toast[]>([])

  function push(text: string, severity: ToastSeverity = 'info', duration = 3500): number {
    const id = _nextId++
    const toast: Toast = { id, text, severity }
    toasts.value.push(toast)
    if (duration > 0) {
      toast.timer = window.setTimeout(() => dismiss(id), duration)
    }
    return id
  }

  function dismiss(id: number): void {
    const idx = toasts.value.findIndex(t => t.id === id)
    if (idx === -1) return
    const [removed] = toasts.value.splice(idx, 1)
    if (removed?.timer) window.clearTimeout(removed.timer)
  }

  function clear(): void {
    for (const t of toasts.value) if (t.timer) window.clearTimeout(t.timer)
    toasts.value = []
  }

  // 便捷 helpers
  const info    = (text: string, ms?: number) => push(text, 'info',    ms)
  const success = (text: string, ms?: number) => push(text, 'success', ms)
  const error   = (text: string, ms?: number) => push(text, 'error',   ms)

  return { toasts, push, dismiss, clear, info, success, error }
})
